import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URLS, BASE_URL } from '../../apiConfig';

// Parsea accesorios desde JSON o CSV con compatibilidad hacia atrás
// Devuelve siempre: { nombre, cantidad (disponible actual), cantidad_total (máximo original) }
const parseAccesoriosActivo = (raw) => {
    if (!raw || raw.trim() === '') return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map(a => ({
                nombre: a.nombre,
                cantidad: Math.max(0, a.cantidad ?? 0),
                cantidad_total: a.cantidad_total ?? a.cantidad ?? 0
            }));
        }
    } catch {}
    return raw.split(',').map(a => a.trim()).filter(a => a)
              .map(nombre => ({ nombre, cantidad: 1, cantidad_total: 1 }));
};

function MovimientoForm({ onMovimientoRegistrado }) {
    const [formData, setFormData] = useState({
        activoId: '',
        destinoId: '',
        tipo_movimiento: 'salida',
        receptorId: '',
        observacion: '',
        cantidad_movimiento: 1
    });

    const [activos, setActivos] = useState([]);
    const [destinos, setDestinos] = useState([]);
    const [receptores, setReceptores] = useState([]);
    const [loadingMasters, setLoadingMasters] = useState(true);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    // Cada item: { nombre, cantidadDisponible, cantidadMovimiento }
    const [accesoriosCheck, setAccesoriosCheck] = useState([]);

    useEffect(() => {
        const fetchMasters = async () => {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            try {
                const [act, dest, recep] = await Promise.all([
                    axios.get(API_URLS.ACTIVOS, config),
                    axios.get(`${BASE_URL}/destinos`, config),
                    axios.get(`${BASE_URL}/receptores`, config)
                ]);
                setActivos(act.data);
                setDestinos(dest.data);
                setReceptores(recep.data);
            } catch (error) {
                setMessage('Error al cargar datos locales.');
                setIsError(true);
            } finally {
                setLoadingMasters(false);
            }
        };
        fetchMasters();
    }, []);

    const getActivoDetails = (id) => activos.find(a => a.id === parseInt(id));

    const imprimirComprobante = (datos) => {
        const ventana = window.open('', '_blank');
        ventana.document.write(`
            <html>
                <head>
                    <title>Comprobante de Movimiento - Scaglia</title>
                    <style>
                        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; padding: 30px; line-height: 1.6; }
                        .header { border-bottom: 3px solid #333; padding-bottom: 10px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
                        .title { font-size: 24px; font-weight: bold; text-transform: uppercase; }
                        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 30px; }
                        .item { border-bottom: 1px solid #eee; padding: 5px 0; }
                        .label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
                        .value { font-size: 16px; display: block; }
                        .accesorios-box { background: #f9f9f9; padding: 15px; border-radius: 8px; margin-bottom: 30px; }
                        .acc-item { display: inline-block; margin: 4px 8px 4px 0; padding: 3px 10px; background: #333; color: #FFD700; border-radius: 20px; font-size: 13px; }
                        .signatures { margin-top: 80px; display: flex; justify-content: space-between; }
                        .sig-line { border-top: 1px solid #333; width: 200px; text-align: center; padding-top: 5px; font-size: 12px; }
                        @media print { .no-print { display: none; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="title">Movimiento de ${datos.tipo_movimiento === 'salida' ? 'Salida' : 'Entrada'}</div>
                        <div>ID Mov: #${Math.floor(Math.random() * 10000)}</div>
                    </div>
                    <div class="info-grid">
                        <div class="item"><span class="label">Fecha y Hora</span><span class="value">${new Date().toLocaleString()}</span></div>
                        <div class="item"><span class="label">Equipo / Activo</span><span class="value">${datos.codigo_producto} - ${datos.nombre_activo}</span></div>
                        <div class="item"><span class="label">Cantidad</span><span class="value">${datos.cantidad} Unidad(es)</span></div>
                        <div class="item"><span class="label">Destino</span><span class="value">${datos.destino}</span></div>
                        <div class="item"><span class="label">Responsable</span><span class="value">${datos.receptor}</span></div>
                    </div>
                    <div class="accesorios-box">
                        <span class="label">Accesorios ${datos.tipo_movimiento === 'salida' ? 'que se llevan' : 'que regresan'}</span>
                        <div style="margin-top:8px">
                            ${datos.accesorios_movimiento && datos.accesorios_movimiento.length > 0
                                ? datos.accesorios_movimiento.map(a => `<span class="acc-item">${a.nombre} (${a.cantidad})</span>`).join('')
                                : '<span style="color:#999">Sin accesorios registrados</span>'
                            }
                        </div>
                    </div>
                    <div class="item"><span class="label">Observaciones</span><p class="value">${datos.observacion || 'Sin observaciones adicionales'}</p></div>
                    <div class="signatures">
                        <div class="sig-line">Firma Entrega Depósito</div>
                        <div class="sig-line">Firma Responsable</div>
                    </div>
                </body>
            </html>
        `);
        ventana.document.close();
        setTimeout(() => { ventana.print(); }, 500);
    };

    const handleActivoChange = (e) => {
        const id = e.target.value;
        setFormData(prev => ({ ...prev, activoId: id }));
        const activo = activos.find(a => a.id === parseInt(id));
        if (activo && activo.accesorios) {
            const lista = parseAccesoriosActivo(activo.accesorios).map(acc => ({
                nombre: acc.nombre,
                cantidadDisponible: acc.cantidad,       // cuántas hay ahora en depósito
                cantidadTotal: acc.cantidad_total,       // total original (máximo posible)
                cantidadMovimiento: 0
            }));
            setAccesoriosCheck(lista);
        } else {
            setAccesoriosCheck([]);
        }
    };

    const handleAccesorioQty = (index, valor, tipoMov) => {
        setAccesoriosCheck(prev => prev.map((a, i) => {
            if (i !== index) return a;
            const raw = parseInt(valor) || 0;
            let max;
            if (tipoMov === 'salida') {
                // No puede llevarse más de lo que hay disponible
                max = a.cantidadDisponible;
            } else {
                // No puede devolver más de lo que está afuera
                const cantidadAfuera = a.cantidadTotal - a.cantidadDisponible;
                max = Math.max(0, cantidadAfuera);
            }
            const qty = Math.min(max, Math.max(0, raw));
            return { ...a, cantidadMovimiento: qty };
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const activo = getActivoDetails(formData.activoId);
        const cantidadMov = parseInt(formData.cantidad_movimiento);
        const disponible = activo.cantidad ?? 0;
        const total = activo.cantidad_total ?? disponible;
        const afuera = Math.max(0, total - disponible);

        if (formData.tipo_movimiento === 'salida' && cantidadMov > disponible) {
            setIsError(true);
            setMessage(`❌ Stock insuficiente. Disponible en depósito: ${disponible}`);
            return;
        }
        if (formData.tipo_movimiento === 'entrada' && cantidadMov > afuera) {
            setIsError(true);
            setMessage(`❌ No podés devolver más de lo que salió. Actualmente afuera: ${afuera}`);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const accesoriosMovimiento = accesoriosCheck
                .filter(a => a.cantidadMovimiento > 0)
                .map(a => ({ nombre: a.nombre, cantidad: a.cantidadMovimiento }));

            const dataToSend = {
                activo_id: activo.id,
                codigo_producto: activo.codigo,
                destino: destinos.find(d => d.id === parseInt(formData.destinoId))?.nombre,
                tipo_movimiento: formData.tipo_movimiento,
                cantidad: cantidadMov,
                receptor: receptores.find(r => r.id === parseInt(formData.receptorId))?.nombre,
                observacion: formData.observacion,
                accesorios_movimiento: accesoriosMovimiento
            };

            await axios.post(`${BASE_URL}/destinos/registrar-movimiento`, dataToSend, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Actualización local del stock del activo
            setActivos(prevActivos => prevActivos.map(a => {
                if (a.id !== activo.id) return a;
                // Actualizar cantidad de accesorios en JSON local
                const accParsed = parseAccesoriosActivo(a.accesorios);
                accesoriosMovimiento.forEach(mov => {
                    const acc = accParsed.find(x => x.nombre === mov.nombre);
                    if (acc) {
                        acc.cantidad = formData.tipo_movimiento === 'salida'
                            ? Math.max(0, acc.cantidad - mov.cantidad)
                            : acc.cantidad + mov.cantidad;
                    }
                });
                return {
                    ...a,
                    cantidad: formData.tipo_movimiento === 'salida' ? a.cantidad - cantidadMov : a.cantidad + cantidadMov,
                    accesorios: JSON.stringify(accParsed)
                };
            }));

            setMessage('✅ Movimiento registrado con éxito.');
            setIsError(false);

            if (window.confirm("¿Desea imprimir el comprobante?")) {
                imprimirComprobante({ ...dataToSend, nombre_activo: activo.nombre });
            }

            setFormData(prev => ({ ...prev, activoId: '', observacion: '', cantidad_movimiento: 1 }));
            setAccesoriosCheck([]);

        } catch (error) {
            setIsError(true);
            setMessage('Error al registrar movimiento.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage('');
                setIsError(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    if (loadingMasters) return (
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-[2rem] shadow-sm border">
            <div className="w-10 h-10 border-4 border-gray-100 border-t-[#FFD700] rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-400 font-bold uppercase text-[10px] tracking-widest">Sincronizando Localmente...</p>
        </div>
    );

    return (
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden mb-10">
            {/* Header */}
            <div className={`p-6 border-b transition-colors duration-500 ${formData.tipo_movimiento === 'salida' ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${formData.tipo_movimiento === 'salida' ? 'text-red-400' : 'text-green-500'}`}>
                            Logística de Equipos
                        </span>
                        <h4 className="text-xl font-black text-gray-800 flex items-center gap-2 mt-1">
                            <i className={`bi ${formData.tipo_movimiento === 'salida' ? 'bi-arrow-up-right-circle' : 'bi-arrow-down-left-circle'} text-2xl`}></i>
                            Registro de {formData.tipo_movimiento === 'salida' ? 'Salida' : 'Entrada'}
                        </h4>
                    </div>
                </div>
            </div>

            <div className="p-8">
                {message && (
                    <div className={`${isError ? 'bg-red-50 text-red-600' : 'bg-gray-900 text-[#FFD700]'} p-4 rounded-2xl mb-8 flex items-center justify-between shadow-sm text-sm font-bold animate-fadeIn`}>
                        <div className="flex items-center">
                            <i className={`bi ${isError ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-3`}></i>
                            {message}
                        </div>
                        <button onClick={() => { setMessage(''); setIsError(false); }} className="ml-4 hover:opacity-70 transition-opacity">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

                        {/* Tipo de Operación */}
                        <div className="col-span-full md:col-span-4 lg:col-span-3">
                            <label className="label-modern">Tipo de Operación</label>
                            <div className="bg-gray-100 p-1 rounded-2xl flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => { setFormData({...formData, tipo_movimiento: 'salida'}); setAccesoriosCheck([]); setFormData(prev => ({...prev, tipo_movimiento: 'salida', activoId: ''})); }}
                                    className={`flex-1 py-2 rounded-xl text-[11px] font-black uppercase tracking-tighter transition-all ${formData.tipo_movimiento === 'salida' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-400'}`}
                                >
                                    Salida
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setFormData(prev => ({...prev, tipo_movimiento: 'entrada', activoId: ''})); setAccesoriosCheck([]); }}
                                    className={`flex-1 py-2 rounded-xl text-[11px] font-black uppercase tracking-tighter transition-all ${formData.tipo_movimiento === 'entrada' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-400'}`}
                                >
                                    Entrada
                                </button>
                            </div>
                        </div>

                        {/* Selección de Activo */}
                        <div className="col-span-full md:col-span-8 lg:col-span-9">
                            <label className="label-modern">Equipo / Activo Seleccionado</label>
                            <select className="input-modern font-bold" name="activoId" value={formData.activoId} onChange={handleActivoChange} required>
                                <option value="">--- Buscar equipo en inventario ---</option>
                                {activos.map(a => (
                                    <option key={a.id} value={a.id} disabled={formData.tipo_movimiento === 'salida' && a.cantidad <= 0}>
                                        {a.codigo} | {a.nombre} (Stock: {a.cantidad})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* SECCIÓN DE ACCESORIOS CON CANTIDADES */}
                        {accesoriosCheck.length > 0 && (
                            <div className="col-span-full bg-gray-50 p-5 rounded-3xl border border-dashed border-gray-200">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3 block">
                                    Accesorios — {formData.tipo_movimiento === 'salida'
                                        ? 'Indicar cuántos se llevan (máx: disponibles en depósito)'
                                        : 'Indicar cuántos regresan (máx: los que están afuera)'}
                                </label>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {accesoriosCheck.map((acc, index) => {
                                        const cantidadAfuera = acc.cantidadTotal - acc.cantidadDisponible;
                                        const esSalida = formData.tipo_movimiento === 'salida';
                                        const maxPermitido = esSalida ? acc.cantidadDisponible : Math.max(0, cantidadAfuera);
                                        const sinMovimiento = maxPermitido === 0;

                                        return (
                                            <div key={index} className={`border rounded-2xl p-3 flex flex-col gap-1 ${sinMovimiento ? 'bg-gray-100 opacity-60' : 'bg-white border-gray-200'}`}>
                                                <p className="text-xs font-black text-gray-700 leading-tight">{acc.nombre}</p>
                                                {esSalida ? (
                                                    <p className="text-[10px] text-gray-400">
                                                        En depósito: <span className="font-bold text-green-600">{acc.cantidadDisponible}</span>
                                                        <span className="text-gray-300"> / {acc.cantidadTotal} total</span>
                                                    </p>
                                                ) : (
                                                    <p className="text-[10px] text-gray-400">
                                                        Afuera: <span className="font-bold text-orange-500">{cantidadAfuera}</span>
                                                        <span className="text-gray-300"> (de {acc.cantidadTotal})</span>
                                                    </p>
                                                )}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={maxPermitido}
                                                    value={acc.cantidadMovimiento}
                                                    disabled={sinMovimiento}
                                                    onChange={(e) => handleAccesorioQty(index, e.target.value, formData.tipo_movimiento)}
                                                    className="input-modern text-center font-bold text-sm py-1 mt-1 disabled:opacity-40"
                                                    placeholder="0"
                                                />
                                                {sinMovimiento && (
                                                    <p className="text-[9px] text-center text-gray-400 italic">
                                                        {esSalida ? 'Sin stock' : 'Todo en depósito'}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Cantidad */}
                        <div className="col-span-full md:col-span-3">
                            <label className="label-modern">Cantidad a Mover</label>
                            {(() => {
                                const activo = getActivoDetails(formData.activoId);
                                const esSalida = formData.tipo_movimiento === 'salida';
                                const disponible = activo?.cantidad ?? 0;
                                const total = activo?.cantidad_total ?? disponible;
                                const afuera = Math.max(0, total - disponible);
                                const maxPermitido = esSalida ? disponible : afuera;
                                return (
                                    <>
                                        <input
                                            type="number"
                                            name="cantidad_movimiento"
                                            className="input-modern text-center font-bold"
                                            value={formData.cantidad_movimiento}
                                            onChange={(e) => {
                                                const v = Math.min(maxPermitido, Math.max(1, parseInt(e.target.value) || 1));
                                                setFormData(prev => ({ ...prev, cantidad_movimiento: v }));
                                            }}
                                            min="1"
                                            max={maxPermitido}
                                            required
                                        />
                                        {activo && (
                                            <p className="text-[10px] text-gray-400 mt-1 text-center">
                                                {esSalida
                                                    ? <>En depósito: <span className="font-bold text-green-600">{disponible}</span> / {total} total</>
                                                    : <>Afuera: <span className="font-bold text-orange-500">{afuera}</span> / {total} total — máx. a devolver: {afuera}</>
                                                }
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Destino */}
                        <div className="col-span-full md:col-span-4">
                            <label className="label-modern">Destino del Equipo</label>
                            <select className="input-modern" name="destinoId" value={formData.destinoId} onChange={handleChange} required>
                                <option value="">Seleccione lugar...</option>
                                {destinos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                            </select>
                        </div>

                        {/* Receptor */}
                        <div className="col-span-full md:col-span-5">
                            <label className="label-modern">Responsable del Traslado</label>
                            <select className="input-modern" name="receptorId" value={formData.receptorId} onChange={handleChange} required>
                                <option value="">Seleccione persona...</option>
                                {receptores.map(r => <option key={r.id} value={r.id}>{r.nombre} {r.apellido}</option>)}
                            </select>
                        </div>

                        {/* Observación */}
                        <div className="col-span-full">
                            <label className="label-modern">Notas Adicionales</label>
                            <textarea className="input-modern min-h-[80px]" name="observacion" value={formData.observacion} onChange={handleChange} placeholder="Detalles sobre el estado del equipo..."></textarea>
                        </div>

                        {/* Botón */}
                        <div className="col-span-full mt-4">
                            <button
                                type="submit"
                                className={`btn-corporate w-full py-4 text-sm flex items-center justify-center gap-3 ${formData.tipo_movimiento === 'salida' ? '!bg-red-600' : '!bg-green-600'}`}
                                disabled={loading}
                            >
                                {loading ? 'Validando Stock...' : (
                                    <>
                                        <i className="bi bi-shield-check"></i>
                                        Confirmar Movimiento
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Stock Info Footer */}
            {formData.activoId && (
                <div className="bg-gray-50 p-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border text-gray-400">
                            <i className="bi bi-box-seam"></i>
                        </div>
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-tight">
                            Status actual en depósito:
                        </span>
                    </div>
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black ${getActivoDetails(formData.activoId)?.cantidad > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {getActivoDetails(formData.activoId)?.cantidad} DISPONIBLES
                    </span>
                </div>
            )}
        </div>
    );
}

export default MovimientoForm;
